import React, { useEffect, useState } from "react";
import { HardDrive, Images } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../software/api";
import { Modal } from "./Dialogs";

// Save to the computer: Chrome/Edge let you pick the folder (Desktop, USB, server share); other browsers download.
async function saveToComputer(blob, name) {
  if (window.showSaveFilePicker) {
    try {
      const ext = blob.type === "image/jpeg" ? ".jpg" : ".png";
      const h = await window.showSaveFilePicker({ suggestedName: name + ext, types: [{ description: `${ext.slice(1).toUpperCase()} picture`, accept: { [blob.type]: [ext] } }] });
      const w = await h.createWritable(); await w.write(blob); await w.close(); return true;
    } catch (e) { if (e.name === "AbortError") return false; }
  }
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${name}${blob.type === "image/jpeg" ? ".jpg" : ".png"}`; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000); return true;
}

export function SaveAsDialog({ s, defaultName, uploadDataUrl, onClose }) {
  const [shots, setShots] = useState(null);
  const [fmt, setFmt] = useState("png");
  const [name, setName] = useState(defaultName);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setShots({ png: s.exportSel("png"), jpeg: s.exportSel("jpeg") });
    api("GET", "/design/folders").then(setFolders).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const shot = shots?.[fmt];
  const clean = (name.trim() || "picture").replace(/[\\/:*?"<>|]+/g, "");
  const toPc = async () => { setBusy(true); try { if (await saveToComputer(await (await fetch(shot.url)).blob(), clean)) { toast.success("Picture saved"); onClose(); } } catch (e) { toast.error(e.message); } finally { setBusy(false); } };
  const toLib = async () => {
    setBusy(true);
    try { const url = await uploadDataUrl(shot.url, `${clean}.${fmt === "jpeg" ? "jpg" : "png"}`); await api("POST", "/design/images", { url, name: clean, folder }); toast.success(`Saved to My Pictures${folder ? ` › ${folders.find((f) => f.id === folder)?.name}` : ""}`); onClose(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal title="Save selection as a picture" onClose={onClose} testid="ds-saveas-dialog" width="max-w-2xl">
      {!shots ? <div className="text-[13px] text-slate-500">Preparing…</div> : !shot ? <div className="text-[13px] text-slate-500">Select one or more items on the page first.</div> : (
        <div className="flex gap-4 text-[13px]">
          <div className="w-56 h-56 shrink-0 grid place-items-center rounded-md bg-[repeating-conic-gradient(#e5e7eb_0_25%,#fff_0_50%)] bg-[length:16px_16px] border border-slate-200 p-2">
            <img src={shot.url} alt="" className="max-w-full max-h-full object-contain" data-testid="ds-saveas-preview" /></div>
          <div className="flex-1 space-y-3">
            <label className="block"><span className="font-bold">Picture name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="ds-saveas-name" className="mt-1 w-full h-9 border border-slate-300 rounded-md px-2" /></label>
            <div><div className="font-bold mb-1">Type</div><div className="flex gap-1">
              {[["png", "PNG (see-through background)"], ["jpeg", "JPG (white background)"]].map(([k, t]) => (
                <button key={k} onClick={() => setFmt(k)} data-testid={`ds-saveas-fmt-${k}`} className={`px-2.5 py-1.5 rounded-md border text-[12px] font-bold transition-colors ${fmt === k ? "bg-[#21a6ec] text-white border-[#21a6ec]" : "bg-white border-slate-300 hover:bg-slate-50"}`}>{t}</button>))}
            </div><div className="text-[11px] text-slate-500 mt-1">{shot.w} × {shot.h} px</div></div>
            <button onClick={toPc} disabled={busy} data-testid="ds-saveas-computer" className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md bg-[#222736] text-white font-bold hover:bg-black disabled:opacity-50 transition-colors">
              <HardDrive className="h-4 w-4" />Save to my computer…<span className="ml-auto text-[11px] font-normal text-slate-300">Desktop, USB, server</span></button>
            <div className="flex gap-1">
              <select value={folder} onChange={(e) => setFolder(e.target.value)} data-testid="ds-saveas-folder" className="flex-1 h-10 border border-slate-300 rounded-md px-2 bg-white">
                <option value="">Unsorted</option>{folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}</select>
              <button onClick={toLib} disabled={busy} data-testid="ds-saveas-library" className="flex items-center gap-2 px-3 rounded-md bg-[#0b6623] text-white font-bold hover:bg-[#08501b] disabled:opacity-50 transition-colors"><Images className="h-4 w-4" />Save to My Pictures</button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
